// Zapis stanu gry w localStorage (wersjonowany JSON).
//
// Zapisujemy TYLKO dane domeny (wybrana plansza i konstrukcja per plansza).
// Nigdy nie zapisujemy obiektow Plancka ani stanu symulacji - to potwierdza
// decyzje D9 z ARCHITECTURE.md.
//
// localStorage moze byc niedostepny (tryb prywatny, brak zgody). Wtedy zapis
// jest po cichu pomijany - gra dziala dalej, tylko bez pamieci.

import { cloneBlueprint } from '../domain/blueprint';
import type { RobotBlueprint } from '../domain/types';

export const STORAGE_KEY = 'robot-lab.state.v1';
export const SCHEMA_VERSION = 2;

export interface SavedState {
  schemaVersion: number;
  /** Id wybranej planszy (np. "step-v1"). */
  selectedChallengeId: string;
  /** Konstrukcja per plansza - klucz to id planszy, wartosc to blueprint. */
  blueprints: Record<string, RobotBlueprint>;
}

/** Bezpieczny dostep do localStorage - w razie problemu zwraca null. */
export function safeStorage(): Storage | null {
  try {
    const storage = globalThis.localStorage;
    if (!storage) return null;
    // Sonda zapisu: w trybie prywatnym sam odczyt moze dzialac, a zapis rzucic.
    const probe = '__robotlab_probe__';
    storage.setItem(probe, '1');
    storage.removeItem(probe);
    return storage;
  } catch {
    return null;
  }
}

function isPartInstance(value: unknown): boolean {
  if (typeof value !== 'object' || value === null) return false;
  const part = value as Record<string, unknown>;
  return (
    typeof part.id === 'string' &&
    typeof part.typeId === 'string' &&
    typeof part.gridX === 'number' &&
    typeof part.gridY === 'number' &&
    typeof part.rotation === 'number'
  );
}

function isConnection(value: unknown): boolean {
  if (typeof value !== 'object' || value === null) return false;
  const conn = value as Record<string, unknown>;
  const ref = (entry: unknown): boolean => {
    if (typeof entry !== 'object' || entry === null) return false;
    const socket = entry as Record<string, unknown>;
    return typeof socket.partId === 'string' && typeof socket.socketId === 'string';
  };
  return ref(conn.a) && ref(conn.b);
}

/** Strukturalny strażnik: czy wartosc wyglada jak poprawny RobotBlueprint. */
export function isRobotBlueprint(value: unknown): value is RobotBlueprint {
  if (typeof value !== 'object' || value === null) return false;
  const blueprint = value as Record<string, unknown>;
  if (blueprint.schemaVersion !== 2) return false;
  if (!Array.isArray(blueprint.parts) || !blueprint.parts.every(isPartInstance)) return false;
  if (!Array.isArray(blueprint.connections) || !blueprint.connections.every(isConnection)) {
    return false;
  }
  if (!Array.isArray(blueprint.drives)) return false;
  return true;
}

/** Wczytuje zapisany stan. Zwraca null przy braku zapisu lub uszkodzonych danych. */
export function loadState(storage: Storage | null = safeStorage()): SavedState | null {
  if (!storage) return null;
  let raw: string | null = null;
  try {
    raw = storage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;

  const state = parsed as Record<string, unknown>;
  if (state.schemaVersion !== SCHEMA_VERSION) return null;
  if (typeof state.selectedChallengeId !== 'string') return null;
  if (typeof state.blueprints !== 'object' || state.blueprints === null) return null;

  const blueprints: Record<string, RobotBlueprint> = {};
  for (const [id, blueprint] of Object.entries(state.blueprints as Record<string, unknown>)) {
    if (isRobotBlueprint(blueprint)) blueprints[id] = cloneBlueprint(blueprint);
  }

  return {
    schemaVersion: SCHEMA_VERSION,
    selectedChallengeId: state.selectedChallengeId,
    blueprints,
  };
}

/** Zapisuje stan. Zwraca false, gdy zapis niemozliwy (brak localStorage/kwota). */
export function saveState(
  selectedChallengeId: string,
  blueprints: Record<string, RobotBlueprint>,
  storage: Storage | null = safeStorage(),
): boolean {
  if (!storage) return false;

  const payload: SavedState = {
    schemaVersion: SCHEMA_VERSION,
    selectedChallengeId,
    blueprints: Object.fromEntries(
      Object.entries(blueprints).map(([id, blueprint]) => [id, cloneBlueprint(blueprint)]),
    ),
  };

  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(payload));
    return true;
  } catch {
    return false;
  }
}

/** Usuwa zapis (np. przycisk "wyczysc pamiec"). */
export function clearState(storage: Storage | null = safeStorage()): void {
  if (!storage) return;
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // ignorujemy - brak zapisu to nie blad gry
  }
}
